import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Package, Truck, ChevronDown, ChevronUp, CreditCard, AlertTriangle, Globe } from 'lucide-react';
import { carrierTrackingUrl, carrierFor } from '../lib/carrierLink';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  placed: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-green-100 text-green-700',
  confirmed: 'bg-green-100 text-green-700',
  cancellation_requested: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABEL = {
  pending: 'placed',
  cancellation_requested: 'cancellation requested',
};

const PAYMENT_LABEL = {
  card: 'Credit / Debit Card',
  crypto: 'Crypto',
  complimentary: 'Complimentary',
};

function formatAddr(addr) {
  if (!addr || !addr.address) return null;
  const name = [addr.first_name, addr.last_name].filter(Boolean).join(' ');
  return {
    name,
    company: addr.company,
    line1: addr.address,
    line2: addr.address_line_2,
    line3: [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', '),
    phone: addr.phone,
    email: addr.email,
    country: addr.country,
  };
}

// Detect "ship-to ≠ bill-to" — looks at the structured fields the user
// entered (we ignore phone & company since those legitimately vary).
function hasAddressMismatch(order) {
  const a = order?.shipping_address || {};
  const b = order?.billing_address || {};
  if (!a.address || !b.address) return false;
  const norm = (s) => (s || '').toString().trim().toLowerCase();
  return (
    norm(a.address) !== norm(b.address) ||
    norm(a.city) !== norm(b.city) ||
    norm(a.state) !== norm(b.state) ||
    norm(a.postal_code) !== norm(b.postal_code)
  );
}

// Detect "ship-to country ≠ IP-derived country" — the backend records
// `compliance.ack_country_code` at checkout from the IP geo lookup. Older
// orders without that field cannot be evaluated; we return false.
function hasCountryMismatch(order) {
  const ipCountry = (order?.ip_country_code || '').toUpperCase();
  const shipCountry = ((order?.shipping_address?.country) || 'US').toUpperCase();
  if (!ipCountry) return false;
  return ipCountry !== shipCountry;
}

export default function AdminOrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchOrders();
  }, [user, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true });
      setOrders(data.orders);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status }, { withCredentials: true });
      toast.success(`Order marked as ${status}`); fetchOrders();
    } catch { toast.error('Failed to update'); }
  };

  const updateTracking = async (orderId) => {
    const num = trackingInputs[orderId];
    if (!num?.trim()) return;
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status: 'shipped', tracking_number: num }, { withCredentials: true });
      toast.success('Tracking saved & order shipped'); fetchOrders();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-12">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-muted animate-pulse rounded mb-3" />)}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-orders-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,40%)] mb-2 font-body">Admin</p>
        <h1 className="text-4xl tracking-tight font-light text-foreground font-heading">Manage Orders</h1>
        <p className="mt-2 text-base text-muted-foreground font-body">{orders.length} total orders</p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-16"><Package className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">No orders yet.</p></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="hidden sm:grid grid-cols-6 gap-4 px-4 py-3 bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b text-center">
              <span>Order No.</span><span>Date</span><span>Status</span><span>Total</span><span>Tracking</span><span>Expand</span>
            </div>
            {orders.map(order => {
              const ship = formatAddr(order.shipping_address);
              const bill = formatAddr(order.billing_address);
              const isExpanded = expanded === order.id;
              const trackingHref = carrierTrackingUrl(order.tracking_number, order.shipping_carrier_code);
              const addrMismatch = hasAddressMismatch(order);
              const countryMismatch = hasCountryMismatch(order);
              return (
                <div key={order.id} className="border-b last:border-0" data-testid={`admin-order-${order.id}`}>
                  <button
                    type="button"
                    className="w-full text-center grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-4 px-4 py-3 items-center hover:bg-muted/20"
                    onClick={() => setExpanded(p => p === order.id ? null : order.id)}
                    data-testid={`expand-order-${order.id}`}
                  >
                    <span className="text-sm font-medium text-foreground inline-flex items-center justify-center gap-1.5">
                      #{order.id}
                      {(addrMismatch || countryMismatch || order.account_refund_flag) && (
                        <span title={[addrMismatch && 'Shipping ≠ Billing', countryMismatch && 'Ship-to country ≠ IP country', order.account_refund_flag && 'Account refund rate >10%'].filter(Boolean).join(' • ')} data-testid={`fraud-flag-${order.id}`}>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        </span>
                      )}
                    </span>
                    <span className="text-xs sm:text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</span>
                    <Badge className={`${STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-700'} text-xs justify-self-center`} data-testid={`order-status-${order.id}`}>{STATUS_LABEL[order.status] || order.status}</Badge>
                    <span className="text-sm font-medium">${order.total?.toFixed(2)}</span>
                    <span className="text-[11px] font-mono break-all" data-testid={`admin-order-tracking-${order.id}`}>
                      {order.tracking_number ? (
                        trackingHref ? (
                          <a
                            href={trackingHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[hsl(210,45%,40%)] hover:underline"
                            title={`Track via ${carrierFor(order.tracking_number) || 'carrier'}`}
                            data-testid={`admin-order-tracking-link-${order.id}`}
                          >{order.tracking_number}</a>
                        ) : <span>{order.tracking_number}</span>
                      ) : <span className="italic text-muted-foreground">Pre-Shipment</span>}
                    </span>
                    <div className="col-span-2 sm:col-span-1">{isExpanded ? <ChevronUp className="h-4 w-4 inline text-muted-foreground" /> : <ChevronDown className="h-4 w-4 inline text-muted-foreground" />}</div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-muted/10 space-y-4">
                      {(addrMismatch || countryMismatch || order.account_refund_flag) && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`fraud-banner-${order.id}`}>
                          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Review recommended</p>
                          <ul className="list-disc pl-5 mt-1 space-y-0.5">
                            {addrMismatch && <li>Shipping address does not match billing address.</li>}
                            {countryMismatch && (
                              <li className="flex items-center gap-1"><Globe className="h-3 w-3" /> Ship-to country <strong>{(ship?.country || 'US').toUpperCase()}</strong> ≠ IP country <strong>{(order.ip_country_code || '?').toUpperCase()}</strong></li>
                            )}
                            {order.account_refund_flag && (
                              <li>This account has a <strong>{order.account_refund_rate_percent}%</strong> refund/cancel rate (threshold 10%).</li>
                            )}
                          </ul>
                        </div>
                      )}
                      <div className="pt-2 flex flex-wrap gap-4 text-xs text-muted-foreground font-body">
                        <span><strong className="text-foreground">Customer:</strong> {order.customer_name || order.email || '—'}</span>
                        <span><strong className="text-foreground">Email:</strong> {order.email}</span>
                        {order.phone && <span><strong className="text-foreground">Phone:</strong> {order.phone}</span>}
                        {order.company && <span><strong className="text-foreground">Company:</strong> {order.company}</span>}
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Items</p>
                        {order.items?.map((item, i) => (
                          <div key={`${item.product_id || 'item'}-${item.variant || ''}-${i}`} className="flex justify-between text-sm">
                            <Link
                              to={`/product/${item.product_id}`}
                              className="text-[hsl(210,45%,40%)] hover:underline"
                              data-testid={`admin-order-item-link-${order.id}-${i}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.name}{item.variant ? ` (${item.variant})` : ''} × {item.quantity}
                            </Link>
                            <span>${item.subtotal?.toFixed(2)}</span>
                          </div>
                        ))}
                        {order.shipping_cost !== undefined && (
                          <div className="flex justify-between text-xs text-muted-foreground pt-1">
                            <span>{order.shipping_label || 'Shipping'}</span>
                            <span>{order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost.toFixed(2)}`}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                          <span>Order Total</span>
                          <span>${order.total?.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="pt-2 border-t" data-testid={`admin-order-payment-${order.id}`}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Payment Method</p>
                        <p className="text-sm text-foreground font-body flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          {PAYMENT_LABEL[order.payment_method] || (order.total === 0 ? 'Complimentary' : 'Credit / Debit Card')}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Shipping Address</p>
                          {ship ? (
                            <div className="text-xs text-foreground font-body space-y-0.5">
                              {ship.name && <p className="font-medium">{ship.name}</p>}
                              {ship.company && <p className="text-muted-foreground">{ship.company}</p>}
                              <p>{ship.line1}</p>
                              {ship.line2 && <p>{ship.line2}</p>}
                              <p>{ship.line3}</p>
                            </div>
                          ) : <p className="text-xs text-muted-foreground italic">—</p>}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Billing Address</p>
                          {bill ? (
                            <div className="text-xs text-foreground font-body space-y-0.5">
                              {bill.name && <p className="font-medium">{bill.name}</p>}
                              {bill.company && <p className="text-muted-foreground">{bill.company}</p>}
                              <p>{bill.line1}</p>
                              {bill.line2 && <p>{bill.line2}</p>}
                              <p>{bill.line3}</p>
                              {bill.phone && <p className="text-muted-foreground">Phone: {bill.phone}</p>}
                              {bill.email && <p className="text-muted-foreground">Email: {bill.email}</p>}
                            </div>
                          ) : <p className="text-xs text-muted-foreground italic">—</p>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                        <span className="text-xs text-muted-foreground mr-1">Set status:</span>
                        <Button size="sm" variant={order.status === 'placed' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => updateOrderStatus(order.id, 'placed')} data-testid={`set-placed-${order.id}`}>Placed</Button>
                        <Button size="sm" variant={order.status === 'processing' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => updateOrderStatus(order.id, 'processing')} data-testid={`set-processing-${order.id}`}>Processing</Button>
                        <Button size="sm" variant={order.status === 'shipped' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => updateOrderStatus(order.id, 'shipped')} data-testid={`set-shipped-${order.id}`}>Shipped</Button>
                        <Button
                          size="sm"
                          variant={order.status === 'cancelled' ? 'default' : 'outline'}
                          className={`h-7 text-xs ${order.status === 'cancelled' ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                          onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          data-testid={`set-cancelled-${order.id}`}
                        >
                          Cancel
                        </Button>
                        <div className="flex items-center gap-1 sm:ml-auto">
                          <Input placeholder="Tracking #" className="h-7 w-40 text-xs" value={trackingInputs[order.id] ?? order.tracking_number ?? ''} onChange={e => setTrackingInputs(p => ({ ...p, [order.id]: e.target.value }))} data-testid={`tracking-input-${order.id}`} />
                          <Button size="sm" className="h-7 text-xs bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" onClick={() => updateTracking(order.id)} data-testid={`save-tracking-${order.id}`}>
                            <Truck className="h-3 w-3 mr-1" /> Ship
                          </Button>
                        </div>
                      </div>
                      {order.tracking_number && (
                        <p className="text-xs text-green-600 flex items-center gap-1"><Truck className="h-3 w-3" /> Tracking: {order.tracking_number}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
